// ---------------------------------------------------------------------------
// F5 (WonBanking arc) — developer trigger for a pack-designated generation.
//
// Follows the `window.__cogoKgForE2E` dev-bridge precedent (App.tsx): a
// DEV-build-only window hook so an operator/orchestrator session can start a
// LIVE Studio generation bound to a released Design System pack without any
// creator UI. The caller supplies the release manifest JSON (fetched from
// GET /api/design-system/pack-lifecycle?releaseId=… with the service token,
// which the browser must never hold) plus the expected identity pin; the
// fail-closed resolver then verifies content/package hashes in-browser
// before the generation may start — a tampered or wrong manifest never
// reaches the generation path.
//
// Usage (browser devtools console on a signed-in DEV Studio):
//   await window.__cogoStartAppGenerationWithDesignSystemPackRelease(
//     '우리은행 영업점 찾기 앱 ...', releaseManifestJson,
//     { releaseId: 'ds-release-…', packageId: 'wonbanking-woori-design-system' },
//   )
// ---------------------------------------------------------------------------

import type { ExpectedPackReleaseIdentity } from './design-system-pack-release-binding'

export const DESIGN_SYSTEM_PACK_RELEASE_DEV_TRIGGER_KEY =
  '__cogoStartAppGenerationWithDesignSystemPackRelease' as const

type DevTriggerWindow = Window & {
  [DESIGN_SYSTEM_PACK_RELEASE_DEV_TRIGGER_KEY]?: (
    prompt: string,
    releaseManifest: unknown,
    expected: ExpectedPackReleaseIdentity,
  ) => Promise<unknown>
}

/**
 * Install the DEV-only trigger. No-ops outside DEV builds and outside a
 * browser. Idempotent. Heavy modules load lazily on first invocation so the
 * hook adds nothing to the initial bundle graph.
 */
export function installDesignSystemPackReleaseDevTrigger(): void {
  if (typeof window === 'undefined') return
  if (!import.meta.env.DEV) return
  const devWindow = window as DevTriggerWindow
  if (devWindow[DESIGN_SYSTEM_PACK_RELEASE_DEV_TRIGGER_KEY]) return
  devWindow[DESIGN_SYSTEM_PACK_RELEASE_DEV_TRIGGER_KEY] = async (
    prompt: string,
    releaseManifest: unknown,
    expected: ExpectedPackReleaseIdentity,
  ) => {
    const [{ resolveDesignSystemPackReleaseBinding }, generation] =
      await Promise.all([
        import('./design-system-pack-release-binding'),
        import('@/features/chat/services/start-app-generation-from-prompt'),
      ])
    const binding = await resolveDesignSystemPackReleaseBinding(
      releaseManifest,
      expected,
    )
    return generation.startAppGenerationFromPrompt(prompt, {
      designSystemPackRelease: binding,
    })
  }
}

export function uninstallDesignSystemPackReleaseDevTrigger(): void {
  if (typeof window === 'undefined') return
  delete (window as DevTriggerWindow)[
    DESIGN_SYSTEM_PACK_RELEASE_DEV_TRIGGER_KEY
  ]
}
